// FASE 11 (Luglio 2026): @ts-nocheck rimosso
/**
 * nativePush.ts — Capacitor Push Notifications Integration
 *
 * Gestisce le notifiche push native su Android/iOS tramite
 * il plugin @capacitor/push-notifications.
 *
 * Funzionalità:
 * - Richiesta permessi notifica (Android 13+ POST_NOTIFICATIONS)
 * - Registrazione token FCM e invio al backend
 * - Gestione notifiche in foreground (mostra local notification con suono/vibrazione)
 * - Gestione notifiche in background (tap = deep link)
 * - Rinnovo automatico token FCM
 * - Suono e vibrazione su tutti i canali
 *
 * Architettura:
 *   Capacitor App → FCM Token → POST /api/push/subscribe
 *   Server → FCM → Dispositivo → Capacitor Push Notifications Plugin
 *   Plugin → Evento 'pushNotificationReceived' → Local Notification (foreground)
 *   Plugin → Evento 'pushNotificationActionPerformed' → Deep Link (tap)
 */

import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import {
  PushNotifications,
  type Token,
  type PushNotificationSchema,
  type ActionPerformed,
} from "@capacitor/push-notifications";
import { LocalNotifications } from "@capacitor/local-notifications";
import { apiFetch } from "@/api/core/apiFetch";
// FEAT CONTENT-READS: tap su notizia/broadcast = lettura (chi ha letto)
import { extractReadableContent, markContentRead } from "@/services/contentReads";

// ─── Costanti ─────────────────────────────────────────────────────────────────

const FCM_TOKEN_STORAGE_KEY = "rideconnect_fcm_token";
const FCM_REGISTERED_KEY = "rideconnect_fcm_registered";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isNativePlatform(): boolean {
  return Capacitor.isNativePlatform();
}

function getPlatform(): string {
  if (!Capacitor.isNativePlatform()) return "web";
  if (Capacitor.getPlatform() === "ios") return "ios";
  return "android";
}

// ─── BUG #4 FIX: Crea i canali di notifica Android prima di register() ────────

async function createNotificationChannels(): Promise<void> {
  if (Capacitor.getPlatform() !== "android") return;

  try {
    // Canale principale per notifiche generali
    await LocalNotifications.createChannel({
      id: "rideconnect_notifications",
      name: "Notifiche RideConnect",
      description:
        "Notifiche generali da admin, eventi, chapter e altri utenti",
      importance: 5, // IMPORTANCE_HIGH → heads-up notification con suono e vibrazione
      sound: "default",
      vibration: true,
      lights: true,
      lightColor: "#6366f1", // viola RideConnect
    });

    // Canale separato per notifiche urgenti (es. evento cancellato, sicurezza)
    await LocalNotifications.createChannel({
      id: "rideconnect_urgent",
      name: "Notifiche Urgenti RideConnect",
      description:
        "Avvisi urgenti: cancellazione evento, sicurezza, broadcast admin",
      importance: 5,
      sound: "default",
      vibration: true,
      lights: true,
      lightColor: "#ef4444", // rosso per urgenza
    });

    console.log("[NativePush] ✅ Notification channels created");
  } catch (err: any) {
    console.error("[NativePush] Failed to create channels:", err?.message);
  }
}
// ─── BUG #9 FIX: Retry su token FCM non ancora arrivato ──────────────────────

const TOKEN_RETRY_INTERVAL_MS = 3000;
const TOKEN_MAX_RETRIES = 10;

async function waitForTokenAndRegister(): Promise<void> {
  let retries = 0;

  while (retries < TOKEN_MAX_RETRIES) {
    // FIX PUSH-403: logout mentre il retry loop è attivo → fermati subito.
    if (!hasAuthToken()) {
      console.log(
        "[NativePush] ⏭️ Logout rilevato — stop retry registrazione FCM",
      );
      return;
    }

    const token = await getStoredToken();
    if (token) {
      console.log("[NativePush] ✅ Token disponibile, invio al server...");
      const ok = await registerFcmTokenOnServer(token);
      if (ok) {
        await markRegistered();
        return;
      }
    }

    console.log(
      `[NativePush] ⏳ Token non ancora disponibile, retry ${retries + 1}/${TOKEN_MAX_RETRIES}...`,
    );
    await new Promise((r) => setTimeout(r, TOKEN_RETRY_INTERVAL_MS));
    retries++;

    // Forza una nuova registrazione se il token non arriva dopo 3 tentativi
    if (retries % 3 === 0) {
      try {
        await PushNotifications.register();
      } catch {
        /* ignore */
      }
    }
  }

  console.error("[NativePush] ❌ Token FCM mai ricevuto dopo tutti i retry");
}
async function getStoredToken(): Promise<string | null> {
  try {
    const result = await Preferences.get({ key: FCM_TOKEN_STORAGE_KEY });
    return result.value || null;
  } catch {
    return localStorage.getItem(FCM_TOKEN_STORAGE_KEY);
  }
}

async function storeToken(token: string): Promise<void> {
  try {
    await Preferences.set({ key: FCM_TOKEN_STORAGE_KEY, value: token });
  } catch {
    localStorage.setItem(FCM_TOKEN_STORAGE_KEY, token);
  }
}

async function isAlreadyRegistered(): Promise<boolean> {
  try {
    const result = await Preferences.get({ key: FCM_REGISTERED_KEY });
    return result.value === "true";
  } catch {
    return localStorage.getItem(FCM_REGISTERED_KEY) === "true";
  }
}

async function markRegistered(): Promise<void> {
  try {
    await Preferences.set({ key: FCM_REGISTERED_KEY, value: "true" });
  } catch {
    localStorage.setItem(FCM_REGISTERED_KEY, "true");
  }
}

// ─── Registrazione token FCM sul backend ──────────────────────────────────────

/**
 * FIX PUSH-403 (Ago 2026): dopo il logout il token FCM restava in
 * Preferences e waitForTokenAndRegister() lo re-inviava al server SENZA
 * Authorization header → 403 CSRF a raffica ogni 3 secondi (10 retry).
 * Ora la registrazione è bloccata finché l'utente non è autenticato.
 */
function hasAuthToken(): boolean {
  try {
    const tok = localStorage.getItem("rideconnect_token");
    return Boolean(tok && tok.length > 0);
  } catch {
    return false;
  }
}

export async function registerFcmTokenOnServer(
  token: string,
): Promise<boolean> {
  // FIX PUSH-403: senza sessione attiva non chiamare l'API — il server
  // risponderebbe 403 (CSRF) e il retry loop spamerebbe i log.
  if (!hasAuthToken()) {
    console.log(
      "[NativePush] ⏭️ Utente non autenticato — registrazione FCM rinviata",
    );
    return false;
  }

  try {
    const platform = getPlatform();
    await apiFetch("/api/push/subscribe", {
      method: "POST",
      body: JSON.stringify({
        fcm_token: token,
        platform,
      }),
    });
    console.log(`[NativePush] ✅ FCM token registered on server (${platform})`);
    return true;
  } catch (err: any) {
    console.error(
      "[NativePush] ❌ Failed to register FCM token:",
      err?.message,
    );
    return false;
  }
}

// ─── Gestione notifiche in foreground ─────────────────────────────────────────

/**
 * Quando l'app è in foreground, le notifiche FCM NON vengono mostrate
 * automaticamente. Dobbiamo creare una Local Notification con suono e vibrazione.
 */
async function showForegroundNotification(
  notification: PushNotificationSchema,
): Promise<void> {
  const { title, body, data } = notification;

  try {
    await LocalNotifications.schedule({
      notifications: [
        {
          id: Date.now(),
          title: title || "RideConnect",
          body: body || "",
          sound: "default", // Suono di sistema
          smallIcon: "ic_stat_icon_config_sample",
          iconColor: "#6366f1",
          schedule: { at: new Date() }, // immediata
          extra: data || {},
          channelId: "rideconnect_notifications",
          actionTypeId: "",
          // Android: priorità massima per heads-up notification
          ...(Capacitor.getPlatform() === "android" ? {} : {}),
        },
      ],
    });
    console.log("[NativePush] 🔔 Foreground notification shown:", title);
  } catch (err: any) {
    console.error(
      "[NativePush] Failed to show foreground notification:",
      err?.message,
    );
  }
}

// ─── Gestione tap sulla notifica (deep link) ──────────────────────────────────

/**
 * Quando l'utente tappa una notifica (sia fredda che calda),
 * navighiamo al deep link contenuto nei data.
 */
function handleNotificationTap(action: ActionPerformed): void {
  const data = action.notification.data as Record<string, unknown> | undefined;
  let url = (data?.url as string | undefined) || "/";

  // FEAT CONTENT-READS (16 Set 2026): tap su notizia/broadcast = lettura.
  // Segnala al server che l'utente ha aperto il contenuto → l'admin vede
  // "chi ha letto" nel pannello (best-effort, fire-and-forget).
  const readable = extractReadableContent(data);
  if (readable) {
    void markContentRead(readable.kind, readable.id);
  }

  // Dealer Task Center: action button o flag openTaskCenter
  if (action.actionId === "open_task_center" || data?.openTaskCenter === true) {
    url = "/dealer/dashboard?taskCenter=open";
  }

  // FIX DEEPLINK (Ago 2026): mappa gli URL legacy delle connessioni biker
  // (notifiche inviate prima del fix) verso la nuova CerchiaPage.
  const LEGACY_URL_MAP: Record<string, string> = {
    "/connections/pending": "/cerchia?tab=richieste",
    "/connections": "/cerchia",
  };
  if (LEGACY_URL_MAP[url]) url = LEGACY_URL_MAP[url];

  console.log("[NativePush] 👆 Notification tapped:", action.actionId, url);

  if (url) {
    // Navigazione SPA: dispatch dell'evento ascoltato da App.tsx (NativeBridgeHandler)
    // che usa useNavigate di React Router (corretto per lo SPA routing).
    // Fallback: history.pushState + popstate per compatibilità.
    const cleanUrl = url.startsWith("/") ? url : `/${url}`;
    try {
      window.dispatchEvent(
        new CustomEvent("rideconnect:navigate", { detail: { url: cleanUrl } }),
      );
      // FIX: se il router non ha ancora montato il listener (app appena
      // aperta da notifica fredda), il fallback pushState assicura comunque
      // la destinazione.
      window.history.pushState({}, "", cleanUrl);
      window.dispatchEvent(new PopStateEvent("popstate"));
    } catch (err: any) {
      console.error("[NativePush] Navigation failed:", err?.message);
    }
  }
}

// ─── Reset / resync (cambio account) ─────────────────────────────────────────

/**
 * Resetta lo stato di registrazione FCM locale (da chiamare al logout).
 * NOTA: il token FCM resta nel DB legato all'utente — al prossimo login
 * resyncTokenForCurrentUser() lo ri-associa all'utente corrente.
 */
export async function resetNativePush(): Promise<void> {
  try {
    await Preferences.remove({ key: FCM_REGISTERED_KEY });
  } catch {
    localStorage.removeItem(FCM_REGISTERED_KEY);
  }
}

/**
 * FIX CAMBIO ACCOUNT (Ago 2026): ri-invia il token FCM al server per
 * l'utente attualmente loggato.
 *
 * Senza questo passaggio, se due account condividono lo stesso dispositivo,
 * il token resta associato SOLO al primo utente che lo ha registrato: il
 * secondo riceve le notifiche in-app ma NON quelle di sistema in background.
 */
export async function resyncTokenForCurrentUser(): Promise<boolean> {
  const token = await getStoredToken();
  if (!token) return false;
  const ok = await registerFcmTokenOnServer(token);
  if (ok) await markRegistered();
  return ok;
}

// ─── Inizializzazione principale ──────────────────────────────────────────────

// FIX DOPPIO-INIT (Ago 2026): App.tsx chiama initNativePush a OGNI cambio
// utente; senza questo flag i listener FCM venivano registrati N volte
// (tap notifica -> N navigazioni duplicate).
let _listenersAttached = false;

export async function initNativePush(): Promise<void> {
  // Solo su piattaforma nativa (Android/iOS)
  if (!isNativePlatform()) {
    console.debug(
      "[NativePush] ⏭️ Web platform — skipping native push (using Web Push instead)",
    );
    return;
  }

  // FIX PUSH-403: App.tsx chiama initNativePush a ogni cambio utente,
  // incluso il logout. Senza sessione non registrare nulla: il retry loop
  // invierebbe POST /api/push/subscribe senza auth → 403 a raffica.
  if (!hasAuthToken()) {
    console.debug(
      "[NativePush] ⏭️ Utente non autenticato — push native rinviata",
    );
    return;
  }

  const platform = getPlatform();
  console.log(`[NativePush] 🚀 Initializing native push for ${platform}...`);

  try {
    // 0. BUG #4 FIX: Crea i canali Android PRIMA di register()
    //    Senza questa chiamata, Android 8+ scarta silenziosamente le notifiche
    //    perché il channelId non esiste → nessun suono, nessuna vibrazione.
    await createNotificationChannels();

    // 1. Richiedi permessi notifica (Android 13+ / iOS)
    const permResult = await PushNotifications.requestPermissions();
    console.log("[NativePush] Permission status:", permResult.receive);

    if (permResult.receive !== "granted") {
      console.warn("[NativePush] ⚠️ Notification permission not granted");
      // Non blocchiamo: l'utente può riprovare più tardi
    }

    if (_listenersAttached) {
      // Listener già attivi: evita duplicati. Il token viene comunque
      // ri-sincronizzato dal chiamante con resyncTokenForCurrentUser().
      await PushNotifications.register().catch(() => {});
      return;
    }
    _listenersAttached = true;

    // 2. REGISTRA I LISTENER PRIMA di register()
    // Fix race condition: il token FCM può arrivare prima che i listener
    // siano attivi, causando la perdita del token e notifiche mute.
    PushNotifications.addListener("registration", async (token: Token) => {
      console.log(
        "[NativePush] 📱 FCM Token received:",
        token.value.substring(0, 20) + "...",
      );

      const storedToken = await getStoredToken();
      if (storedToken !== token.value) {
        await storeToken(token.value);
        const registered = await registerFcmTokenOnServer(token.value);
        if (registered) {
          await markRegistered();
        }
      } else {
        console.log("[NativePush] Token unchanged, already registered");
      }
    });

    PushNotifications.addListener("registrationError", (error: any) => {
      console.error("[NativePush] ❌ Registration error:", error);
    });

    PushNotifications.addListener(
      "pushNotificationReceived",
      async (notification: PushNotificationSchema) => {
        console.log(
          "[NativePush] 📨 Foreground notification:",
          notification.title,
        );
        await showForegroundNotification(notification);
      },
    );

    PushNotifications.addListener(
      "pushNotificationActionPerformed",
      (action: ActionPerformed) => {
        handleNotificationTap(action);
      },
    );

    // 3. SOLO ORA chiama register() — i listener sono già attivi
    await PushNotifications.register();
    console.log("[NativePush] ✅ Registered with APNs/FCM");

    // 4. BUG #9 FIX: Retry per token FCM — se non arriva subito, riprova
    await waitForTokenAndRegister();

    console.log("[NativePush] ✅ Native push initialized successfully");
  } catch (err: any) {
    console.error("[NativePush] ❌ Initialization failed:", err?.message);
  }
}

/**
 * Verifica se le notifiche push native sono supportate su questa piattaforma.
 */
export function isNativePushSupported(): boolean {
  return Capacitor.isNativePlatform();
}

// FIX PUSH-DEFAULT (02 Set 2026): l'utente vuole che la domanda dei permessi
// notifiche compaia SUBITO all'installazione/primo avvio, non dopo il login.
// Questa funzione crea i canali e chiede il permesso di sistema una sola
// volta per sessione; l'utente accetta o rifiuta.
let _bootPermissionAsked = false;
export async function requestNotificationPermissionOnBoot(): Promise<void> {
  if (!isNativePlatform() || _bootPermissionAsked) return;
  _bootPermissionAsked = true;
  try {
    await createNotificationChannels();
    await PushNotifications.requestPermissions();
    console.log("[NativePush] 🔔 Permesso notifiche richiesto al primo avvio");
  } catch {
    /* non bloccare mai il boot per i permessi */
  }
}

/**
 * Verifica lo stato corrente delle notifiche native.
 * Restituisce informazioni su permessi e token.
 */
export async function getNativePushState(): Promise<{
  supported: boolean;
  permission: string;
  token: string | null;
  platform: string;
}> {
  const platform = getPlatform();

  if (!isNativePlatform()) {
    return {
      supported: false,
      permission: "unsupported",
      token: null,
      platform: "web",
    };
  }

  try {
    const permStatus = await PushNotifications.checkPermissions();
    const token = await getStoredToken();

    return {
      supported: true,
      permission: permStatus.receive,
      token,
      platform,
    };
  } catch {
    return {
      supported: true,
      permission: "prompt",
      token: null,
      platform,
    };
  }
}

/**
 * Richiede nuovamente i permessi (utile dopo un rifiuto iniziale).
 */
export async function requestNativePushPermission(): Promise<boolean> {
  if (!isNativePlatform()) return false;

  try {
    const result = await PushNotifications.requestPermissions();
    if (result.receive === "granted") {
      await PushNotifications.register();
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * Listener per notifiche push (esportato per uso in componenti React).
 * Restituisce una funzione di cleanup.
 */
export async function onNativePushNotification(
  callback: (notification: PushNotificationSchema) => void,
): Promise<() => void> {
  if (!isNativePlatform()) {
    return () => {};
  }

  const listener = await PushNotifications.addListener(
    "pushNotificationReceived",
    callback,
  );

  return () => {
    listener.remove();
  };
}
